import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Tabs, 
  Tab, 
  Typography, 
  Grid, 
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Palette as PaletteIcon,
  Code as CodeIcon,
  Preview as PreviewIcon,
  Publish as PublishIcon
} from '@mui/icons-material';
import { db } from '../firebase';
import { collection, getDocs, doc, setDoc, deleteDoc, orderBy } from 'firebase/firestore';
import { HtmlEditor, ImageUploader, ColorPicker } from '../components/WebsiteComponents';

function WebsiteManager() {
  const [tabValue, setTabValue] = useState('pages');
  const [pages, setPages] = useState([]);
  const [sections, setSections] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const [themeConfig, setThemeConfig] = useState({
    primaryColor: '#7C3AED',
    secondaryColor: '#4F46E5',
    font: 'Roboto'
  });

  // Charger les données depuis Firestore
  useEffect(() => {
    const loadData = async () => {
      const pagesSnapshot = await getDocs(collection(db, 'website_pages'));
      setPages(pagesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
      const sectionsSnapshot = await getDocs(collection(db, 'website_sections'));
      setSections(sectionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    
    loadData();
  }, []);

  // Sauvegarder une page
  const savePage = async (pageData) => {
    await setDoc(doc(db, 'website_pages', pageData.id), pageData);
    setPages(pages.map(p => p.id === pageData.id ? pageData : p));
  };

  // Gestion des sections
  const addSection = (type) => {
    const newSection = {
      id: `section-${Date.now()}`,
      type,
      content: '',
      order: sections.length + 1
    };
    setSections([...sections, newSection]);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Gestion du Site Web
      </Typography>
      
      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
        <Tab label="Pages" value="pages" />
        <Tab label="Sections" value="sections" />
        <Tab label="Thème" value="theme" />
        <Tab label="Formulaires" value="forms" />
      </Tabs>
      
      <Box sx={{ mt: 3 }}>
        {tabValue === 'pages' && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Pages du site
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  sx={{ mb: 2 }}
                >
                  Nouvelle page
                </Button>
                
                {pages.map(page => (
                  <Paper 
                    key={page.id} 
                    sx={{ 
                      p: 2, 
                      mb: 1,
                      cursor: 'pointer',
                      backgroundColor: currentPage?.id === page.id ? '#F3F4F6' : 'inherit'
                    }}
                    onClick={() => setCurrentPage(page)}
                  >
                    <Typography>{page.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      /{page.slug}
                    </Typography>
                  </Paper>
                ))}
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={8}>
              {currentPage && (
                <Paper sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h6">
                      Éditer: {currentPage.title}
                    </Typography>
                    <Button variant="contained" startIcon={<SaveIcon />}>
                      Enregistrer
                    </Button>
                  </Box>
                  
                  <TextField
                    label="Titre de la page"
                    fullWidth
                    value={currentPage.title}
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    label="URL (slug)"
                    fullWidth
                    value={currentPage.slug}
                    sx={{ mb: 2 }}
                  />
                  
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Sections de la page
                  </Typography>
                  
                  {/* Liste des sections avec drag & drop */}
                  {currentPage.sections?.map((sectionId, index) => {
                    const section = sections.find(s => s.id === sectionId);
                    return section ? (
                      <Paper key={section.id} sx={{ p: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography>{section.type}</Typography>
                          <Box>
                            <Tooltip title="Monter">
                              <IconButton disabled={index === 0}>
                                <ArrowUpwardIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Descendre">
                              <IconButton disabled={index === currentPage.sections.length - 1}>
                                <ArrowDownwardIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Supprimer">
                              <IconButton>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        
                        {/* Éditeur selon le type de section */}
                        {section.type === 'text' && (
                          <HtmlEditor 
                            value={section.content}
                            onChange={(newContent) => {
                              setSections(sections.map(s => 
                                s.id === section.id ? { ...s, content: newContent } : s
                              ));
                            }}
                          />
                        )}
                        
                        {section.type === 'image' && (
                          <ImageUploader 
                            imageUrl={section.content}
                            onUpload={(url) => {
                              setSections(sections.map(s => 
                                s.id === section.id ? { ...s, content: url } : s
                              ));
                            }}
                          />
                        )}
                      </Paper>
                    ) : null;
                  })}
                  
                  <Button 
                    variant="outlined" 
                    startIcon={<AddIcon />}
                    onClick={() => {
                      // Ouvrir un dialogue pour choisir le type de section
                    }}
                  >
                    Ajouter une section
                  </Button>
                </Paper>
              )}
            </Grid>
          </Grid>
        )}
        
        {tabValue === 'theme' && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Configuration du thème
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <ColorPicker
                  label="Couleur principale"
                  value={themeConfig.primaryColor}
                  onChange={(color) => setThemeConfig({...themeConfig, primaryColor: color})}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ColorPicker
                  label="Couleur secondaire"
                  value={themeConfig.secondaryColor}
                  onChange={(color) => setThemeConfig({...themeConfig, secondaryColor: color})}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Police principale</InputLabel>
                  <Select
                    value={themeConfig.font}
                    label="Police principale"
                    onChange={(e) => setThemeConfig({...themeConfig, font: e.target.value})}
                  >
                    <MenuItem value="Roboto">Roboto</MenuItem>
                    <MenuItem value="Open Sans">Open Sans</MenuItem>
                    <MenuItem value="Montserrat">Montserrat</MenuItem>
                    <MenuItem value="Poppins">Poppins</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                startIcon={<PublishIcon />}
                onClick={() => {
                  // Sauvegarder le thème
                }}
              >
                Publier les changements
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
}

export default WebsiteManager;